import React, { useState, useEffect } from "react";
import "./SalesHistory.css";
import Sidebar from "../Sidebar/Sidebar";
import api from "../../api";
import ReceiptModal from "../ReceiptModal/ReceiptModal";

const ENV = process.env.REACT_APP_ENV || "production";
const IS_DESKTOP = ENV === "desktop";

// ── Network error helper ──
const getNetworkErrorMessage = (err, fallback = "Something went wrong. Please try again.") => {
  const isNetworkError = (
    !navigator.onLine ||
    err.code === "ERR_NETWORK" ||
    err.code === "ERR_INTERNET_DISCONNECTED" ||
    err.code === "ERR_NAME_NOT_RESOLVED" ||
    err.code === "ERR_CONNECTION_REFUSED" ||
    err.code === "ERR_CONNECTION_TIMED_OUT" ||
    err.message === "Network Error" ||
    err.message?.includes("ERR_INTERNET_DISCONNECTED") ||
    err.message?.includes("net::")
  );
  if (isNetworkError) {
    if (IS_DESKTOP) return "🔧 Cannot connect to local server. Please restart the MyApoti desktop app.";
    return "📡 No internet connection. Please check your network and try again.";
  }
  if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
    return "⏱ Request timed out. The server took too long to respond. Please try again.";
  }
  if (err.response) {
    const status = err.response.status;
    const detail = err.response.data?.detail;
    if (status === 401) return "🔒 Session expired. Please log in again.";
    if (status === 403) return `🚫 Access denied. ${detail || "You don't have permission for this action."}`;
    if (status === 404) return `🔍 Not found. ${detail || "The requested resource was not found."}`;
    if (status >= 500) return `🔧 Server error (${status}). Please try again later or contact support.`;
    if (detail) return detail;
  }
  return fallback;
};

const SalesHistory = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  const [receiptSearch, setReceiptSearch] = useState("");
  const [dateSearch, setDateSearch] = useState("");

  useEffect(() => {
    fetchSalesHistory();
  }, []);

  const fetchSalesHistory = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/POS/pos/sales/history?skip=0&limit=20");
      setSales(res.data.sales || []);
    } catch (err) {
      setError(getNetworkErrorMessage(err, "Failed to load sales history."));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const filteredSales = sales.filter((sale) => {
    const receiptMatch =
      receiptSearch === "" ||
      String(sale.receipt_number || sale.sale_id).toLowerCase().includes(receiptSearch.toLowerCase());
    const dateMatch =
      dateSearch === "" ||
      new Date(sale.created_at).toISOString().slice(0, 10) === dateSearch;
    return receiptMatch && dateMatch;
  });

  return (
    <div className="pos-layout">
      {window.innerWidth > 768 && <Sidebar />}

      <div className="sales-history-container pos-container">
        <h2>Sales History</h2>

        <div className="sales-filters">
          <input
            type="text"
            placeholder="Search receipt number..."
            value={receiptSearch}
            onChange={(e) => setReceiptSearch(e.target.value)}
          />
          <input
            type="date"
            value={dateSearch}
            onChange={(e) => setDateSearch(e.target.value)}
          />
        </div>

        {loading && <p>Loading...</p>}

        {error && (
          <div style={{
            background: "#fff5f5", border: "1px solid #dc3545", borderRadius: "8px",
            padding: "0.75rem 1rem", marginBottom: "1rem",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem",
            fontSize: "0.9rem", color: "#dc3545",
          }}>
            <span>{error}</span>
            <button
              onClick={fetchSalesHistory}
              style={{
                background: "#dc3545", color: "white", border: "none",
                borderRadius: "5px", padding: "0.4rem 1rem",
                cursor: "pointer", fontSize: "0.85rem", whiteSpace: "nowrap",
              }}
            >
              🔄 Retry
            </button>
          </div>
        )}

        {!loading && !error && filteredSales.length === 0 && (
          <p>No matching sales found.</p>
        )}

        {filteredSales.map((sale) => (
          <div key={sale.sale_id} className="sale-card">
            <div className="sale-header">
              <span><strong>Receipt:</strong> {sale.receipt_number || sale.sale_id}</span>
              <span><strong>Status:</strong> {sale.status}</span>
              {sale.status === "voided" && sale.voided_by && (
                <span><strong>Voided By:</strong> {sale.voided_by}</span>
              )}
              <span><strong>Date:</strong> {formatDate(sale.created_at)}</span>
              <span><strong>Total:</strong> ₦{sale.total_amount.toFixed(2)}</span>
            </div>

            <div className="sale-items">
              <h4>Items:</h4>
              {sale.items.map((item) => (
                <div key={item.inventory_id} className="sale-item">
                  <span>{item.name}</span>
                  <span>{item.quantity} × ₦{item.unit_price} = ₦{item.subtotal}</span>
                </div>
              ))}
            </div>

            <button className="print-btn" onClick={() => setSelectedReceipt(sale)}>
              Print Receipt
            </button>
          </div>
        ))}
      </div>

      {selectedReceipt && (
        <ReceiptModal sale={selectedReceipt} onClose={() => setSelectedReceipt(null)} />
      )}
    </div>
  );
};

export default SalesHistory;